import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-background hover:bg-primary-hover active:bg-primary-active',
        destructive: 'bg-error text-background hover:bg-error/90 active:bg-error/80',
        outline: 'border border-border bg-transparent hover:bg-surface hover:text-foreground',
        secondary: 'bg-surface text-foreground hover:bg-surface-hover active:bg-surface-active',
        ghost: 'hover:bg-surface hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /**
   * When true, the button will render as a Slot, allowing you to pass
   * a custom element as a child (e.g., a Link component).
   */
  asChild?: boolean;
}

/**
 * Button component with multiple variants and sizes.
 *
 * Built on Radix UI's Slot primitive for composition flexibility.
 *
 * @example
 * <Button variant="default">Click me</Button>
 * <Button variant="outline" size="lg">Large Outline</Button>
 * <Button asChild><Link to="/home">Home</Link></Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
